import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Navigate } from 'react-router-dom';
import { toast } from "../components/common/Notifier";


export const ProtectedRoute = ({ children, adminOnly = false, allowGuest = true }) => {
  const { user, isAdmin, isGuest, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  // Гости могут проходить на определенные страницы
  if (isGuest && !allowGuest) {
    return <Navigate to="/login" replace />;
  }

  // Если админка и не админ
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Если нет пользователя и не гость
  if (!user && !isGuest) {
    return <Navigate to="/login" replace />;
  }

  return children;
};
const LANGUAGES = {
  en: { code: 'en', label: 'English', flag: '🇬🇧' },
  ru: { code: 'ru', label: 'Русский' },
  uk: { code: 'uk', label: 'Українська', flag: '🇺🇦' }
};

export const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {t, language, changeLanguage} = useTranslation(); // ← Хук
  const {login, guestLogin} = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password);
    if (result.success) {
      toast.success(t('common.success'));
      navigate('/dashboard');
    } else {
      toast.error(result.error || t('error.general'));
    }
    setLoading(false);
  };

  const handleGuestLogin = () => {
    guestLogin();
    toast.success(t('common.success'));
    navigate('/home');
  };

  return (
      <div
          className="min-h-screen flex flex-col relative"
          style={{
            backgroundImage: `url('/mooms.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
      >
        <div className="absolute inset-0 bg-black/50"></div>

        {/* Основная часть: занимает всё место над футером и центрирует форму */}
        <main className="relative z-10 flex-1 flex items-center justify-center px-4 py-4">
          <div className="w-full max-w-md space-y-4">

            {/* Переключатель языков: по центру */}
            <div className="flex justify-center gap-2">
              {Object.values(LANGUAGES).map((lang) => (
                  <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${
                          language === lang.code
                              ? 'bg-white/90 text-blue-600 shadow-lg'
                              : 'bg-white/30 text-white hover:bg-white/50'
                      }`}
                  >
                    {lang.flag} {lang.label}
                  </button>
              ))}
            </div>

            {/* Логотип компактнее */}
            <div className="text-center">
              <img
                  src="/moom1.png"
                  alt="Moomintroll"
                  className="w-20 h-20 mx-auto mb-2 rounded-full shadow-2xl border-4 border-white object-cover"
              />
              <h1 className="text-3xl font-bold text-white drop-shadow-lg">MoomDoom</h1>
              <p className="mt-1 text-base text-white/90 drop-shadow-lg">{t('login.title')}</p>
              <p className="text-xs text-white/80 italic drop-shadow-lg">{t('login.subtitle')}</p>
            </div>

            {/* Форма */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    👤 {t('login.username')}
                  </label>
                  <input
                      type="text"
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="mt-1 block w-full px-3 py-2.5 border border-gray-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-blue-500"
                      placeholder={t('login.username_placeholder')}
                      autoComplete="username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    🔒 {t('login.password')}
                  </label>
                  <div className="relative mt-1">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full px-3 py-2.5 pr-12 border border-gray-300 rounded-xl bg-white/80 focus:ring-2 focus:ring-blue-500"
                        placeholder={t('login.password_placeholder')}
                        autoComplete="current-password"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <div className="text-right mt-1">
                    <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-800">
                      {t('login.forgot_password')}
                    </Link>
                  </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 px-4 rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all"
                >
                  {loading ? t('common.loading') : `🚀 ${t('login.login_button')}`}
                </button>
              </form>

              {/* Регистрация и гость в одну строку вместо двух широких кнопок */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white/90 text-gray-500">{t('login.or')}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Link
                    to="/register"
                    className="text-center py-2.5 px-3 rounded-xl text-sm text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all"
                >
                  ✍️ {t('login.register_button')}
                </Link>
                <button
                    onClick={handleGuestLogin}
                    className="py-2.5 px-3 rounded-xl text-sm border-2 border-gray-300 text-gray-700 bg-white/80 hover:bg-gray-100 transition-all"
                >
                  👋 {t('login.guest_button')}
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Футер всегда внизу экрана */}
        <footer className="relative z-10 py-3 px-4 text-center text-xs text-white/70 drop-shadow-lg">
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1">
            <Link to="/home" className="hover:text-white transition-colors">
              ← {t('common.back_to_home')}
            </Link>
            <span>© 2026 MoomDoom — {t('login.footer')}</span>
          </div>
        </footer>
      </div>
  );
}